import { createGradientFill } from '../lib/gradient-parser';
import { SHAPES, TYPES } from './constants';
import { createFill } from './utils/fill';
import { resolveRadius } from './utils/radius';
import { resolveStrokeColor, resolveStrokeWidth } from './utils/stroke';

// Create a mapping of shape types to their render functions
const shapeRenderers = {
  [SHAPES.ellipse]: renderEllipse,
  [SHAPES.rect]: renderRect,
  [SHAPES.triangle]: renderTriangle,
  [SHAPES.star]: renderStar,
  [SHAPES.hexagon]: renderHexagon,
  [SHAPES.octagon]: renderOctagon,
  [SHAPES.pentagon]: renderPentagon,
  [SHAPES.cross]: renderCross,
  [SHAPES.heart]: renderHeart,
  [SHAPES.polygon]: renderPolygon,
};

export function renderShape(renderContext, node) {
  if (node.type !== TYPES.shape) return;
  // Get the appropriate renderer or default to renderRect
  const renderer = shapeRenderers[node.shape] || renderRect;
  return renderer(renderContext, node);
}

// Helper function to handle common shape rendering logic
function renderShapeBase(renderContext, node, pathCallback) {
  const { context } = renderContext;
  const { x, y, h, w } = normalizeDimensions(node);
  const strokeWidths = resolveStrokeWidth(node.strokeWidth || 0);
  const strokeColors = resolveStrokeColor(node.stroke);

  const adjustedNode = {
    ...node,
    x: x + strokeWidths.value / 2,
    y: y + strokeWidths.value / 2,
    w: w - strokeWidths.value,
    h: h - strokeWidths.value,
  };
  context.save();
  context.beginPath();
  pathCallback(context, adjustedNode);
  context.closePath();

  const fill = node.fill;
  createFill(renderContext, fill, adjustedNode);

  if (strokeWidths.value && strokeColors.value) {
    context.beginPath();
    pathCallback(context, adjustedNode);
    context.closePath();
    context.strokeStyle = createGradientFill(context, strokeColors.value, adjustedNode) || '';
    context.lineWidth = strokeWidths.value;
    context.stroke();
  }

  context.restore();
}

export function normalizeDimensions(node) {
  let { x, y, w, h } = node;
  if (w < 0) {
    x += w;
    w = Math.abs(w);
  }
  if (h < 0) {
    y += h;
    h = Math.abs(h);
  }
  return { x, y, w, h };
}

export function renderEllipse(renderContext, node) {
  const { context } = renderContext;
  const { x, y, h, w } = normalizeDimensions(node);
  const strokeWidths = resolveStrokeWidth(node.strokeWidth || 0);
  const strokeColors = resolveStrokeColor(node.stroke);

  const adjustedNode = normalizeDimensions({
    ...node,
    x: x + strokeWidths.value / 2,
    y: y + strokeWidths.value / 2,
    w: w - strokeWidths.value,
    h: h - strokeWidths.value,
  });

  context.save();
  context.beginPath();
  context.ellipse(
    adjustedNode.x + adjustedNode.w / 2,
    adjustedNode.y + adjustedNode.h / 2,
    adjustedNode.w / 2,
    adjustedNode.h / 2,
    0,
    0,
    Math.PI * 2
  );
  context.closePath();

  const fill = node.fill;
  createFill(renderContext, fill, adjustedNode);

  if (strokeWidths.value && strokeColors.value) {
    context.beginPath();
    context.ellipse(
      adjustedNode.x + adjustedNode.w / 2,
      adjustedNode.y + adjustedNode.h / 2,
      adjustedNode.w / 2,
      adjustedNode.h / 2,
      0,
      0,
      Math.PI * 2
    );
    context.closePath();

    context.strokeStyle = createGradientFill(context, strokeColors.value, adjustedNode) || '';
    context.lineWidth = strokeWidths.value;

    // Apply stroke dash properties if available
    if (node.strokeDasharray) {
      const dashArray = node.strokeDasharray.split(',').map((val) => parseFloat(val.trim()));
      context.setLineDash(dashArray);
    }

    if (node.strokeDashoffset !== undefined) {
      context.lineDashOffset = node.strokeDashoffset;
    }

    if (node.strokeLinecap) {
      context.lineCap = node.strokeLinecap;
    }

    if (node.strokeLinejoin) {
      context.lineJoin = node.strokeLinejoin;
    }

    context.stroke();
  }

  context.restore();
}

export function renderRect(renderContext, node) {
  const { context } = renderContext;
  const { x, y, h, w } = normalizeDimensions(node);
  const strokeWidths = resolveStrokeWidth(node.strokeWidth || 0);
  const strokeColors = resolveStrokeColor(node.stroke);
  const adjustedNode = {
    ...node,
    x: x + Math.max(strokeWidths.left, 0) / 2,
    y: y + Math.max(strokeWidths.top, 0) / 2,
    w: w - (Math.max(strokeWidths.left, 0) + Math.max(strokeWidths.right, 0)) / 2,
    h: h - (Math.max(strokeWidths.top, 0) + Math.max(strokeWidths.bottom, 0)) / 2,
  };

  context.save();
  context.beginPath();
  roundedRect(context, adjustedNode.x, adjustedNode.y, adjustedNode.w, adjustedNode.h, node.radius);
  context.closePath();

  const fill = node.fill;
  createFill(renderContext, fill, adjustedNode);

  drawRoundedRectStroke(context, adjustedNode, strokeColors, strokeWidths);

  context.restore();
}

export function drawRoundedRectStroke(context, node, strokeColors, strokeWidths) {
  const { x, y, h, w } = node;
  const { topLeft, topRight, bottomRight, bottomLeft } = resolveRadius(w, h, node.radius);

  // Only draw strokes if there are non-zero widths
  if (strokeWidths.value === 0) return;
  if (strokeColors.isEmpty) return;

  // Apply stroke opacity if specified
  const globalOpacity = node.opacity !== undefined ? node.opacity : 1;
  const strokeOpacity = node.strokeOpacity !== undefined ? node.strokeOpacity : 1;

  // Save the original global alpha to restore it later
  const originalGlobalAlpha = context.globalAlpha;

  // Set the global alpha for stroke operations
  if (strokeOpacity !== 1 || globalOpacity !== 1) {
    context.globalAlpha = strokeOpacity * globalOpacity;
  }

  // Apply additional stroke properties if specified
  if (node.strokeLinecap) context.lineCap = node.strokeLinecap;
  if (node.strokeLinejoin) context.lineJoin = node.strokeLinejoin;
  if (node.strokeMiterlimit) context.miterLimit = node.strokeMiterlimit;

  // Apply stroke dasharray if specified
  if (node.strokeDasharray && node.strokeDasharray !== '') {
    const dashArray = node.strokeDasharray.split(/[\s,]+/).map(parseFloat);
    context.setLineDash(dashArray);
    if (node.strokeDashoffset) {
      context.lineDashOffset = node.strokeDashoffset;
    }
  }

  // Create gradient-compatible stroke colors
  const gradientStrokeColors = {
    top: createGradientFill(context, strokeColors.top, { x, y, w, h }),
    right: createGradientFill(context, strokeColors.right, { x, y, w, h }),
    bottom: createGradientFill(context, strokeColors.bottom, { x, y, w, h }),
    left: createGradientFill(context, strokeColors.left, { x, y, w, h }),
  };

  // Check if all corners are square (no radius)
  if (topLeft === 0 && topRight === 0 && bottomRight === 0 && bottomLeft === 0) {
    // For square corners, we can use a simpler drawing approach
    // Calculate half line widths for corner adjustments
    const halfTopWidth = strokeWidths.top / 2;
    const halfRightWidth = strokeWidths.right / 2;
    const halfBottomWidth = strokeWidths.bottom / 2;
    const halfLeftWidth = strokeWidths.left / 2;

    if (strokeWidths.top > 0) {
      context.beginPath();
      context.moveTo(x - halfLeftWidth, y);
      context.lineTo(x + w + halfRightWidth, y);
      context.lineWidth = strokeWidths.top;
      context.strokeStyle = gradientStrokeColors.top;
      context.stroke();
    }

    if (strokeWidths.right > 0) {
      context.beginPath();
      context.moveTo(x + w, y + halfTopWidth);
      context.lineTo(x + w, y + h - halfBottomWidth);
      context.lineWidth = strokeWidths.right;
      context.strokeStyle = gradientStrokeColors.right;
      context.stroke();
    }

    if (strokeWidths.bottom > 0) {
      context.beginPath();
      context.moveTo(x + w + halfRightWidth, y + h);
      context.lineTo(x - halfLeftWidth, y + h);
      context.lineWidth = strokeWidths.bottom;
      context.strokeStyle = gradientStrokeColors.bottom;
      context.stroke();
    }

    if (strokeWidths.left > 0) {
      context.beginPath();
      context.moveTo(x, y + h - halfBottomWidth);
      context.lineTo(x, y + halfTopWidth);
      context.lineWidth = strokeWidths.left;
      context.strokeStyle = gradientStrokeColors.left;
      context.stroke();
    }

    return;
  }

  // Helper function to draw a border segment
  const drawBorderSegment = (x1, y1, x2, y2, width, color) => {
    if (width <= 0) return;
    context.beginPath();
    context.moveTo(x1, y1);
    context.lineTo(x2, y2);
    context.lineWidth = width;
    context.strokeStyle = color;
    context.stroke();
  };

  // Top border
  drawBorderSegment(
    x + topLeft,
    y,
    x + w - topRight,
    y,
    strokeWidths.top,
    gradientStrokeColors.top
  );

  // Right border
  drawBorderSegment(
    x + w,
    y + topRight,
    x + w,
    y + h - bottomRight,
    strokeWidths.right,
    gradientStrokeColors.right
  );

  // Bottom border
  drawBorderSegment(
    x + bottomLeft,
    y + h,
    x + w - bottomRight,
    y + h,
    strokeWidths.bottom,
    gradientStrokeColors.bottom
  );

  // Left border
  drawBorderSegment(
    x,
    y + topLeft,
    x,
    y + h - bottomLeft,
    strokeWidths.left,
    gradientStrokeColors.left
  );

  // Helper function to draw corner arc (constant width)
  const drawCornerArc = (cx, cy, radius, startAngle, endAngle, width, color) => {
    // Original check: if (radius <= 0 || width <= 0) return;
    // If width is 0 or negative, do nothing.
    // If radius is 0 (sharp corner), it implies topLeft (from resolveRadius) is 0,
    // so the `if (topLeft > 0)` block for the corner wouldn't be entered.
    // Thus, `radius` here (which is `topLeft`) should be > 0.
    if (width <= 0) return;

    context.beginPath();
    context.arc(cx, cy, radius, startAngle, endAngle);
    context.lineWidth = width;
    context.strokeStyle = color;
    context.stroke();
  };

  // Helper function to draw a tapered stroke segment as a filled path
  const drawFilledTaperingStrokeSegment = (
    cx,
    cy,
    pathRadius,
    sAngle,
    eAngle,
    wStart,
    wEnd,
    color
  ) => {
    // pathRadius is the radius of the centerline of the stroke.
    // wStart, wEnd are full widths at start and end of the segment.

    // If pathRadius is effectively zero (e.g. very small due to topLeft being small),
    // and widths are positive, this will attempt to draw.
    // Covered by the `if (topLeft > 0)` check before calling this.
    if (wStart <= 0 && wEnd <= 0) return; // Nothing to draw if both widths are zero or less.

    context.beginPath();
    context.fillStyle = color;

    // Determine the number of steps for approximation to enhance visual fidelity.
    // More steps result in a smoother curve, crucial for high-quality, "award-winning" visuals.
    // Aim for segments of roughly 1 degree, which generally provides excellent smoothness.
    const angularSweepRad = Math.abs(eAngle - sAngle);
    let calculatedSteps;

    if (angularSweepRad < 1e-6) {
      // If the sweep angle is effectively zero, one segment is sufficient to represent
      // the (degenerate) shape, which would be a line or trapezoid along the radial.
      calculatedSteps = 1;
    } else {
      const degreesPerStepTarget = 1.0; // Target 1 degree per segment.
      const radiansPerStepTarget = (degreesPerStepTarget * Math.PI) / 180.0;
      calculatedSteps = Math.ceil(angularSweepRad / radiansPerStepTarget);
    }

    // The loop for points runs from i = 0 to 'steps'.
    // So, 'steps' here represents the number of segments.
    // Ensure at least 1 segment (which means 2 points will be generated).
    const steps = Math.max(1, calculatedSteps);

    // Generate outer path points
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const angle = sAngle + (eAngle - sAngle) * t;
      let currentWidth = wStart + (wEnd - wStart) * t;
      if (currentWidth < 0) currentWidth = 0; // Ensure width is not negative

      const r_outer = pathRadius + currentWidth / 2;
      const pointX = cx + r_outer * Math.cos(angle);
      const pointY = cy + r_outer * Math.sin(angle);

      if (i === 0) {
        context.moveTo(pointX, pointY);
      } else {
        context.lineTo(pointX, pointY);
      }
    }

    // Generate inner path points (in reverse angular order to close the shape)
    for (let i = steps; i >= 0; i--) {
      const t = i / steps;
      const angle = sAngle + (eAngle - sAngle) * t;
      let currentWidth = wStart + (wEnd - wStart) * t;
      if (currentWidth < 0) currentWidth = 0;

      const r_inner = pathRadius - currentWidth / 2;
      // Prevent inner radius from crossing the center (cx,cy) if width is large relative to pathRadius
      const actual_r_inner = Math.max(0, r_inner);

      const pointX = cx + actual_r_inner * Math.cos(angle);
      const pointY = cy + actual_r_inner * Math.sin(angle);
      context.lineTo(pointX, pointY);
    }

    context.closePath();
    context.fill();
  };

  // Top-left corner
  if (topLeft > 0) {
    const cx = x + topLeft;
    const cy = y + topLeft;
    // `topLeft` (from resolveRadius) is used as the radius for the stroke's centerline path.
    const radius = topLeft;

    const W_L = strokeWidths.left;
    const C_L = gradientStrokeColors.left;
    const W_T = strokeWidths.top;
    const C_T = gradientStrokeColors.top;

    const hasLeftBorder = W_L > 0;
    const hasTopBorder = W_T > 0;

    // Angles for top-left corner segments:
    const angleLeftStart = Math.PI; // 180 degrees (left-most point of the arc)
    const angleMid = Math.PI * 1.25; // 225 degrees (mid-point of the 90-degree corner arc)
    const angleTopEnd = Math.PI * 1.5; // 270 degrees (top-most point of the arc)

    if (hasLeftBorder && !hasTopBorder) {
      // Case: Left border exists, top border does not. Left border tapers into the top area.
      // First 45-degree segment (180 to 225 deg): Draw with full left border width.
      drawCornerArc(cx, cy, radius, angleLeftStart, angleMid, W_L, C_L);
      // Second 45-degree segment (225 to 270 deg): Taper left border from full width (W_L) to 0.
      drawFilledTaperingStrokeSegment(cx, cy, radius, angleMid, angleTopEnd, W_L, 0, C_L);
    } else if (!hasLeftBorder && hasTopBorder) {
      // Case: Top border exists, left border does not. Top border tapers into the left area.
      // First 45-degree segment (180 to 225 deg): Taper top border from 0 (at 180 deg) to full width (W_T at 225 deg).
      drawFilledTaperingStrokeSegment(cx, cy, radius, angleLeftStart, angleMid, 0, W_T, C_T);
      // Second 45-degree segment (225 to 270 deg): Draw with full top border width.
      drawCornerArc(cx, cy, radius, angleMid, angleTopEnd, W_T, C_T);
    } else {
      // Original behavior for other cases:
      // 1. Both borders exist (W_L > 0 && W_T > 0): Draw each segment with its respective border style.
      // 2. No borders exist (W_L <= 0 && W_T <= 0): drawCornerArc will draw nothing for segments with width <= 0.
      drawCornerArc(cx, cy, radius, angleLeftStart, angleMid, W_L, C_L);
      drawCornerArc(cx, cy, radius, angleMid, angleTopEnd, W_T, C_T);
    }
  }

  // Top-right corner
  if (topRight > 0) {
    const cx = x + w - topRight;
    const cy = y + topRight;
    const radius = topRight;

    const W_T = strokeWidths.top;
    const C_T = gradientStrokeColors.top;
    const W_R = strokeWidths.right;
    const C_R = gradientStrokeColors.right;

    const hasTopBorder = W_T > 0;
    const hasRightBorder = W_R > 0;

    // Angles for top-right corner segments (counter-clockwise):
    const angleTopStart = -Math.PI * 0.5; // 270 degrees (top-most point of the arc)
    const angleMid = -Math.PI * 0.25; // 315 degrees (mid-point of the 90-degree corner arc)
    const angleRightEnd = 0; // 0 or 360 degrees (right-most point of the arc)
    // Alternative positive angles:
    // const angleTopStart = Math.PI * 1.5;
    // const angleMid = Math.PI * 1.75;
    // const angleRightEnd = Math.PI * 2; // or 0

    if (hasTopBorder && !hasRightBorder) {
      // Case: Top border exists, right border does not. Top border tapers into the right area.
      // First 45-degree segment (270 to 315 deg): Draw with full top border width.
      drawCornerArc(cx, cy, radius, angleTopStart, angleMid, W_T, C_T);
      // Second 45-degree segment (315 to 0 deg): Taper top border from full width (W_T) to 0.
      drawFilledTaperingStrokeSegment(cx, cy, radius, angleMid, angleRightEnd, W_T, 0, C_T);
    } else if (!hasTopBorder && hasRightBorder) {
      // Case: Right border exists, top border does not. Right border tapers into the top area.
      // First 45-degree segment (270 to 315 deg): Taper right border from 0 to full width (W_R).
      drawFilledTaperingStrokeSegment(cx, cy, radius, angleTopStart, angleMid, 0, W_R, C_R);
      // Second 45-degree segment (315 to 0 deg): Draw with full right border width.
      drawCornerArc(cx, cy, radius, angleMid, angleRightEnd, W_R, C_R);
    } else {
      // Original behavior for other cases:
      // 1. Both borders exist (W_T > 0 && W_R > 0): Draw each segment with its respective border style.
      // 2. No borders exist (W_T <= 0 && W_R <= 0): drawCornerArc will draw nothing.
      drawCornerArc(cx, cy, radius, angleTopStart, angleMid, W_T, C_T);
      drawCornerArc(cx, cy, radius, angleMid, angleRightEnd, W_R, C_R);
    }
  }

  // Bottom-right corner
  if (bottomRight > 0) {
    const cx = x + w - bottomRight;
    const cy = y + h - bottomRight;
    const radius = bottomRight;

    const W_R = strokeWidths.right;
    const C_R = gradientStrokeColors.right;
    const W_B = strokeWidths.bottom;
    const C_B = gradientStrokeColors.bottom;

    const hasRightBorder = W_R > 0;
    const hasBottomBorder = W_B > 0;

    // Angles for bottom-right corner segments (counter-clockwise):
    const angleRightStart = 0; // 0 degrees (right-most point of the arc)
    const angleMid = Math.PI * 0.25; // 45 degrees (mid-point)
    const angleBottomEnd = Math.PI * 0.5; // 90 degrees (bottom-most point)

    if (hasRightBorder && !hasBottomBorder) {
      // Case: Right border exists, bottom border does not. Right border tapers into the bottom area.
      // First 45-degree segment (0 to 45 deg): Draw with full right border width.
      drawCornerArc(cx, cy, radius, angleRightStart, angleMid, W_R, C_R);
      // Second 45-degree segment (45 to 90 deg): Taper right border from full width (W_R) to 0.
      drawFilledTaperingStrokeSegment(cx, cy, radius, angleMid, angleBottomEnd, W_R, 0, C_R);
    } else if (!hasRightBorder && hasBottomBorder) {
      // Case: Bottom border exists, right border does not. Bottom border tapers into the right area.
      // First 45-degree segment (0 to 45 deg): Taper bottom border from 0 to full width (W_B).
      drawFilledTaperingStrokeSegment(cx, cy, radius, angleRightStart, angleMid, 0, W_B, C_B);
      // Second 45-degree segment (45 to 90 deg): Draw with full bottom border width.
      drawCornerArc(cx, cy, radius, angleMid, angleBottomEnd, W_B, C_B);
    } else {
      // Original behavior:
      drawCornerArc(cx, cy, radius, angleRightStart, angleMid, W_R, C_R);
      drawCornerArc(cx, cy, radius, angleMid, angleBottomEnd, W_B, C_B);
    }
  }

  // Bottom-left corner
  if (bottomLeft > 0) {
    const cx = x + bottomLeft;
    const cy = y + h - bottomLeft;
    const radius = bottomLeft;

    const W_B = strokeWidths.bottom;
    const C_B = gradientStrokeColors.bottom;
    const W_L = strokeWidths.left;
    const C_L = gradientStrokeColors.left;

    const hasBottomBorder = W_B > 0;
    const hasLeftBorder = W_L > 0;

    // Angles for bottom-left corner segments (counter-clockwise):
    const angleBottomStart = Math.PI * 0.5; // 90 degrees (bottom-most point)
    const angleMid = Math.PI * 0.75; // 135 degrees (mid-point)
    const angleLeftEnd = Math.PI; // 180 degrees (left-most point)

    if (hasBottomBorder && !hasLeftBorder) {
      // Case: Bottom border exists, left border does not. Bottom border tapers into the left area.
      // First 45-degree segment (90 to 135 deg): Draw with full bottom border width.
      drawCornerArc(cx, cy, radius, angleBottomStart, angleMid, W_B, C_B);
      // Second 45-degree segment (135 to 180 deg): Taper bottom border from full width (W_B) to 0.
      drawFilledTaperingStrokeSegment(cx, cy, radius, angleMid, angleLeftEnd, W_B, 0, C_B);
    } else if (!hasBottomBorder && hasLeftBorder) {
      // Case: Left border exists, bottom border does not. Left border tapers into the bottom area.
      // First 45-degree segment (90 to 135 deg): Taper left border from 0 to full width (W_L).
      drawFilledTaperingStrokeSegment(cx, cy, radius, angleBottomStart, angleMid, 0, W_L, C_L);
      // Second 45-degree segment (135 to 180 deg): Draw with full left border width.
      drawCornerArc(cx, cy, radius, angleMid, angleLeftEnd, W_L, C_L);
    } else {
      // Original behavior:
      drawCornerArc(cx, cy, radius, angleBottomStart, angleMid, W_B, C_B);
      drawCornerArc(cx, cy, radius, angleMid, angleLeftEnd, W_L, C_L);
    }
  }

  // Restore the original global alpha after stroke operations
  context.globalAlpha = originalGlobalAlpha;
}

function roundedRect(ctx, x, y, w, h, radius) {
  const { x: normX, y: normY, w: normW, h: normH } = normalizeDimensions({ x, y, w, h });

  if (!radius) {
    ctx.rect(normX, normY, normW, normH);
    return;
  }

  const { topLeft, topRight, bottomRight, bottomLeft } = resolveRadius(normW, normH, radius);

  ctx.moveTo(normX + topLeft, normY);
  ctx.lineTo(normX + normW - topRight, normY);
  ctx.arcTo(normX + normW, normY, normX + normW, normY + topRight, topRight);
  ctx.lineTo(normX + normW, normY + normH - bottomRight);
  ctx.arcTo(normX + normW, normY + normH, normX + normW - bottomRight, normY + normH, bottomRight);
  ctx.lineTo(normX + bottomLeft, normY + normH);
  ctx.arcTo(normX, normY + normH, normX, normY + normH - bottomLeft, bottomLeft);
  ctx.lineTo(normX, normY + topLeft);
  ctx.arcTo(normX, normY, normX + topLeft, normY, topLeft);
}

export function renderTriangle(renderContext, node) {
  renderShapeBase(renderContext, node, (context, node) => {
    const { x, y, w, h } = normalizeDimensions(node);
    context.moveTo(x + w / 2, y);
    context.lineTo(x + w, y + h);
    context.lineTo(x, y + h);
  });
}

export function renderStar(renderContext, node) {
  renderShapeBase(renderContext, node, (context, node) => {
    const { x, y, w, h } = normalizeDimensions(node);
    const spikes = 5;
    const outerRadius = Math.min(w, h) / 2;
    const innerRadius = outerRadius / 2;

    for (let i = 0; i < spikes * 2; i++) {
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const angle = (Math.PI / spikes) * i - Math.PI / 2;
      const px = x + w / 2 + radius * Math.cos(angle);
      const py = y + h / 2 + radius * Math.sin(angle);
      if (i === 0) context.moveTo(px, py);
      else context.lineTo(px, py);
    }
  });
}

export function renderHexagon(renderContext, node) {
  renderShapeBase(renderContext, node, (context, node) => {
    const { x, y, w, h } = normalizeDimensions(node);
    const sideLength = Math.min(w, h) / 2;

    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i;
      const px = x + w / 2 + sideLength * Math.cos(angle);
      const py = y + h / 2 + sideLength * Math.sin(angle);
      if (i === 0) context.moveTo(px, py);
      else context.lineTo(px, py);
    }
  });
}

export function renderOctagon(renderContext, node) {
  renderShapeBase(renderContext, node, (context, node) => {
    const { x, y, w, h } = normalizeDimensions(node);
    const sideLength = Math.min(w, h) / 2.414;

    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI / 4) * i;
      const px = x + w / 2 + sideLength * Math.cos(angle);
      const py = y + h / 2 + sideLength * Math.sin(angle);
      if (i === 0) context.moveTo(px, py);
      else context.lineTo(px, py);
    }
  });
}

export function renderPentagon(renderContext, node) {
  renderShapeBase(renderContext, node, (context, node) => {
    const { x, y, w, h } = normalizeDimensions(node);
    const radius = Math.min(w, h) / 2;

    for (let i = 0; i < 5; i++) {
      const angle = ((Math.PI * 2) / 5) * i - Math.PI / 2;
      const px = x + w / 2 + radius * Math.cos(angle);
      const py = y + h / 2 + radius * Math.sin(angle);
      if (i === 0) context.moveTo(px, py);
      else context.lineTo(px, py);
    }
  });
}

export function renderCross(renderContext, node) {
  renderShapeBase(renderContext, node, (context, node) => {
    const { x, y, w, h } = normalizeDimensions(node);
    const thickness = Math.min(w, h) / 4;

    // Vertical bar
    context.rect(x + w / 2 - thickness / 2, y, thickness, h);
    // Horizontal bar
    context.rect(x, y + h / 2 - thickness / 2, w, thickness);
  });
}

export function renderPolygon(renderContext, node) {
  renderShapeBase(renderContext, node, (context, node) => {
    const { x, y, w, h } = normalizeDimensions(node);
    const points = node.points.split(' ').map((p) => p.split(',').map(Number));
    const minX = Math.min(...points.map((p) => p[0]));
    const minY = Math.min(...points.map((p) => p[1]));
    const maxX = Math.max(...points.map((p) => p[0]));
    const maxY = Math.max(...points.map((p) => p[1]));
    const scaleX = w / (maxX - minX);
    const scaleY = h / (maxY - minY);

    points.forEach(([px, py], i) => {
      const scaledX = x + (px - minX) * scaleX;
      const scaledY = y + (py - minY) * scaleY;
      if (i === 0) {
        context.moveTo(scaledX, scaledY);
      } else {
        context.lineTo(scaledX, scaledY);
      }
    });
  });
}

export function renderHeart(renderContext, node) {
  renderShapeBase(renderContext, node, (context, node) => {
    const { x, y, w, h } = normalizeDimensions(node);
    const scaleX = w / 100;
    const scaleY = h / 100;

    context.moveTo(x + 50 * scaleX, y + 30 * scaleY);
    context.bezierCurveTo(
      x + 80 * scaleX,
      y - 20 * scaleY,
      x + 100 * scaleX,
      y + 50 * scaleY,
      x + 50 * scaleX,
      y + 100 * scaleY
    );
    context.bezierCurveTo(
      x,
      y + 50 * scaleY,
      x + 20 * scaleX,
      y - 20 * scaleY,
      x + 50 * scaleX,
      y + 30 * scaleY
    );
  });
}
